import { Fragment, useState } from "react";
import { Autocomplete, Box, Checkbox, Chip, TextField } from "@mui/material";
import Flex from "@/components/Flex";
import { useSnackbar } from "@/hooks/useSnackbar";
import { StyleAddButton } from "@/pages/app/provider/scheduling/appointment/encounter/default-forms/form-1/StyledAddButton";
import {
  CODE_SEARCH_TAG_HEIGHT_PX,
  CODE_SEARCH_TAG_MAX_WIDTH_PX,
  CODE_SEARCH_VISIBLE_TAGS,
} from "../initial-f2f-code-constant";
import { CodeItem } from "../initial-f2f-types";
import { toCustomCode, uniqueCodes } from "../initial-f2f-utils";
import { useCloseOnScroll } from "../hooks/use-close-on-scroll";

export interface CodeSearchFieldProps {
  options: CodeItem[];
  isLoading: boolean;
  placeholder: string;
  buttonLabel: string;
  onSearch: (value: string) => void;
  addedCodes: CodeItem[];
  onAdd: (codes: CodeItem[]) => void;
  isMultiple?: boolean;
  isSearchOnly?: boolean;
  duplicateMessage: string;
}

const isSameCode = (a: CodeItem, b: CodeItem): boolean => a.value === b.value;

// The theme fixes the input height, so the row stays single-line and overflow shows as "+n".
const SINGLE_LINE_SX = {
  flex: 1,
  minWidth: 0,
  "& .MuiOutlinedInput-root": { flexWrap: "nowrap", overflow: "hidden" },
};

const CodeSearchField = ({
  options,
  isLoading,
  placeholder,
  buttonLabel,
  onSearch,
  addedCodes,
  onAdd,
  isMultiple = true,
  isSearchOnly = false,
  duplicateMessage,
}: CodeSearchFieldProps) => {
  const showSnackbar = useSnackbar();
  const [inputValue, setInputValue] = useState("");
  const [pending, setPending] = useState<CodeItem[]>([]);
  const list = useCloseOnScroll();
  const inputPlaceholder = pending.length ? undefined : placeholder;

  const isAdded = (option: CodeItem) =>
    addedCodes.some((code) => isSameCode(code, option));

  const resetSearch = () => {
    setInputValue("");
    onSearch("");
  };

  const handleChange = (selected: (CodeItem | string)[]) => {
    const hasTyped = selected.some((item) => typeof item === "string");
    const codes = selected.flatMap((item) => {
      if (typeof item !== "string") return [item];
      const typed = isSearchOnly ? undefined : toCustomCode(item);
      return typed ? [typed] : [];
    });
    setPending(uniqueCodes(codes));
    // Picking an option, or committing typed text, empties the search box so the next search starts clean.
    if (hasTyped || isSearchOnly) resetSearch();
  };

  const handleSingleChange = (selected: CodeItem | string | null) => {
    if (typeof selected === "string") {
      const typed = isSearchOnly ? undefined : toCustomCode(selected);
      setPending(typed ? [typed] : []);
      return;
    }
    setPending(selected ? [selected] : []);
    setInputValue(selected?.code ?? "");
  };

  const addPending = () => {
    const canType = !isSearchOnly && (isMultiple || !pending.length);
    const typed = canType ? toCustomCode(inputValue) : undefined;
    const wanted = uniqueCodes(typed ? [...pending, typed] : pending);
    const toAdd = wanted.filter((code) => !isAdded(code));
    if (toAdd.length < wanted.length) {
      showSnackbar({ type: "error", message: duplicateMessage });
    }
    if (toAdd.length) onAdd(toAdd);
    setPending([]);
    resetSearch();
  };

  return (
    <Flex gap={1} alignItems="flex-start" minWidth={0}>
      {isMultiple ? (
        <Autocomplete
          multiple
          freeSolo={!isSearchOnly}
          fullWidth
          disableCloseOnSelect
          size="small"
          open={list.isOpen}
          onOpen={list.open}
          onClose={list.close}
          options={options}
          loading={isLoading}
          filterOptions={(all) => all}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option.code
          }
          getOptionDisabled={isAdded}
          isOptionEqualToValue={(option, selected) =>
            option.value === selected.value
          }
          value={pending}
          inputValue={inputValue}
          sx={SINGLE_LINE_SX}
          limitTags={CODE_SEARCH_VISIBLE_TAGS}
          renderTags={(tags, getTagProps) => (
            <Fragment>
              {tags.slice(0, CODE_SEARCH_VISIBLE_TAGS).map((tag, index) => (
                <Chip
                  size="small"
                  {...getTagProps({ index })}
                  key={tag.value}
                  label={tag.code}
                  sx={{
                    height: CODE_SEARCH_TAG_HEIGHT_PX,
                    maxWidth: CODE_SEARCH_TAG_MAX_WIDTH_PX,
                  }}
                />
              ))}
              {tags.length > CODE_SEARCH_VISIBLE_TAGS && (
                <Box component="span" sx={{ flexShrink: 0, ml: 0.25 }}>
                  {`+${tags.length - CODE_SEARCH_VISIBLE_TAGS}`}
                </Box>
              )}
            </Fragment>
          )}
          onInputChange={(_, next, reason) => {
            if (reason === "reset") return;
            setInputValue(next);
            onSearch(next);
          }}
          onChange={(_, selected) => handleChange(selected)}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option.value}>
              <Checkbox
                size="small"
                checked={selected || isAdded(option)}
                sx={{ mr: 1 }}
              />
              {option.code}
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} placeholder={inputPlaceholder} />
          )}
        />
      ) : (
        <Autocomplete
          freeSolo={!isSearchOnly}
          fullWidth
          size="small"
          open={list.isOpen}
          onOpen={list.open}
          onClose={list.close}
          options={options}
          loading={isLoading}
          filterOptions={(all) => all}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option.code
          }
          getOptionDisabled={isAdded}
          isOptionEqualToValue={(option, selected) =>
            option.value === selected.value
          }
          value={pending[0] ?? null}
          inputValue={inputValue}
          onInputChange={(_, next, reason) => {
            if (reason === "reset") return;
            setInputValue(next);
            setPending([]);
            onSearch(next);
          }}
          onChange={(_, selected) => handleSingleChange(selected)}
          renderInput={(params) => (
            <TextField {...params} placeholder={inputPlaceholder} />
          )}
        />
      )}
      <StyleAddButton
        w="160px"
        flexShrink={0}
        onClick={addPending}
        label={buttonLabel}
      />
    </Flex>
  );
};

export default CodeSearchField;
